import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Box } from "@mui/material";
import { useCallback, useState } from "react";
import { useTheme } from "../../../context/ThemeContext";
import { Currency } from "../../../i18n/Currency";
import { computeWaterfall } from "../../../shared/apportionmentEngine";
import {
  ApportionedAmounts,
  ApportionmentContext,
  ApportionmentItem,
  isCostsItem,
  isQuantumItem,
  itemKey,
} from "../../../shared/apportionmentTypes";
import ApportionmentRow from "./ApportionmentRow";

export interface ApportionmentListProps {
  items: ApportionmentItem[];
  context: ApportionmentContext;
  onItemsChange: (items: ApportionmentItem[]) => void;
  currency: Currency;
  labels: Record<string, string>; // itemKey -> display label
  subtitles?: Record<string, string>; // itemKey -> what the opened row says under its head
  // the rows are shown as they are: not dragged, switched or given an amount
  readOnly?: boolean;
}

function getCap(item: ApportionmentItem, ctx: ApportionmentContext): number {
  if (isCostsItem(item)) {
    switch (item.costs) {
      case "ofSettlement":
        return ctx.caps.ofSettlement;
      case "towardsPayer":
        return ctx.caps.towardsPayer;
      case "generic":
        return ctx.caps.generic;
    }
  }
  if (isQuantumItem(item)) {
    return ctx.caps.quantums[item.quantumId] ?? 0;
  }
  return 0;
}

function getAllocated(
  item: ApportionmentItem,
  amounts: ApportionedAmounts,
): number {
  if (isCostsItem(item)) {
    switch (item.costs) {
      case "ofSettlement":
        return amounts.costs.settlement ?? 0;
      case "towardsPayer":
        return amounts.costs.towardsPayer ?? 0;
      case "generic":
        return amounts.costs.generic ?? 0;
    }
  }
  if (isQuantumItem(item)) {
    return amounts.quantums[item.quantumId] ?? 0;
  }
  return 0;
}

function isUncovered(
  item: ApportionmentItem,
  ctx: ApportionmentContext,
): boolean {
  return isQuantumItem(item) && !(item.quantumId in ctx.caps.quantums);
}

/**
 * What a settlement amount is paid towards, in the order it is paid out: the
 * rows are dragged into another order, and one of them at a time is opened.
 */
const ApportionmentList = ({
  items,
  context,
  onItemsChange,
  currency,
  labels,
  subtitles,
  readOnly = false,
}: ApportionmentListProps) => {
  const { theme } = useTheme();
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const amounts = computeWaterfall(items, context);
  const itemKeys = items.map(itemKey);

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveKey(String(event.active.id));
    setExpandedKey(null);
  }, []);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveKey(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = itemKeys.indexOf(String(active.id));
      const newIndex = itemKeys.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;

      onItemsChange(arrayMove([...items], oldIndex, newIndex));
    },
    [items, itemKeys, onItemsChange],
  );

  const updateItem = useCallback(
    (index: number, update: Partial<ApportionmentItem>) => {
      const updated = items.map((item, i) =>
        i === index ? { ...item, ...update } : item,
      );
      onItemsChange(updated);
    },
    [items, onItemsChange],
  );

  // Compute running available funds for each item position
  const availableFundsAt = (index: number): number => {
    let remaining = context.settlementAmount;
    for (let i = 0; i < index; i++) {
      remaining -= getAllocated(items[i], amounts);
    }
    return Math.max(0, remaining);
  };

  const activeIndex = activeKey ? itemKeys.indexOf(activeKey) : -1;
  const activeItem = activeIndex >= 0 ? items[activeIndex] : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={itemKeys} strategy={verticalListSortingStrategy}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.875 }}>
          {items.map((item, index) => {
            const key = itemKey(item);
            const cap = getCap(item, context);
            const allocated = getAllocated(item, amounts);
            const uncovered = isUncovered(item, context);
            const target =
              item.specificAmount !== undefined ? item.specificAmount : cap;
            const insufficient =
              item.enabled && !uncovered && availableFundsAt(index) < target;

            return (
              <ApportionmentRow
                key={key}
                item={item}
                label={labels[key] ?? key}
                subtitle={subtitles?.[key]}
                cap={cap}
                allocated={allocated}
                insufficientFunds={insufficient}
                uncovered={uncovered}
                currency={currency}
                expanded={expandedKey === key}
                availableFunds={availableFundsAt(index)}
                readOnly={readOnly}
                onToggleEnabled={(enabled) => updateItem(index, { enabled })}
                onToggleExpand={() =>
                  setExpandedKey((prev) => (prev === key ? null : key))
                }
                onSpecificAmountChange={(amount) =>
                  updateItem(index, { specificAmount: amount })
                }
              />
            );
          })}
        </Box>
      </SortableContext>

      <DragOverlay>
        {activeItem ? (
          <Box
            sx={{
              boxShadow: theme.shadows[4],
              transform: "scale(1.02)",
              borderRadius: 2,
            }}
          >
            <ApportionmentRow
              item={activeItem}
              label={labels[itemKey(activeItem)] ?? itemKey(activeItem)}
              subtitle={subtitles?.[itemKey(activeItem)]}
              cap={getCap(activeItem, context)}
              allocated={getAllocated(activeItem, amounts)}
              insufficientFunds={false}
              uncovered={false}
              currency={currency}
              expanded={false}
              availableFunds={0}
              onToggleEnabled={() => {}}
              onToggleExpand={() => {}}
              onSpecificAmountChange={() => {}}
            />
          </Box>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
};

export default ApportionmentList;
